
import { API_URL } from "../config";
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import {
  FaGift,
  FaTag,
  FaCopy,
  FaMoneyBillWave,
  FaUsers,
  FaUserFriends,
  FaUniversity,
  FaUser,
  FaCreditCard,
  FaCheck,
} from "react-icons/fa";
import { toast } from "react-toastify";

export default function ReferralPage() {
  const [promoCode, setPromoCode] = useState("");
  const [referredUsers, setReferredUsers] = useState([]);

  const [bankName, setBankName] = useState("");
  const [accountName, setAccountName] = useState("");
  const [accountNum, setAccountNum] = useState("");
  const [profit, setProfit] = useState(0);

  const [loading, setLoading] = useState(false);
  // const [message, setMessage] = useState("");

  const token = useMemo(() => {
    const tokenString = localStorage.getItem("authToken");
    return tokenString ? JSON.parse(tokenString) : null;
  }, []);

  console.log(token.access);
  // 🔹 Fetch Promo Code + Referred Users
  const fetchPromoData = async () => {
    try {
      const response = await axios.get(`${API_URL}/student/my-promo-code/`, {
        headers: {
          Authorization: `Bearer ${token.access}`,
        },
      });

      setPromoCode(response.data.my_student_code);
      setReferredUsers(response.data.referred_users || []);
    } catch (error) {
      console.log("Error fetching promo data");
    }
  };

  // 🔹 Fetch Account Details
  const fetchAccountDetails = async () => {
    try {
      const response = await axios.get(`${API_URL}/promo-code/`, {
        headers: {
          Authorization: `Bearer ${token.access}`,
        },
      });

      setBankName(response.data.bank_name || "");
      setAccountName(response.data.account_name || "");
      setAccountNum(response.data.account_num || "");
      setProfit(response.data.profit || 0);
    } catch (error) {
      console.log("Error fetching account details");
    }
  };

  const decoded = JSON.parse(atob(token.access.split(".")[1]));
  console.log(decoded);

  // 🔹 Update Account Details
  const handleUpdateAccount = async () => {
    try {
      setLoading(true);

      await axios.put(
        `${API_URL}/promo-code/`,
        {
          bank_name: bankName,
          account_name: accountName,
          account_num: accountNum,
        },
        {
          headers: {
            Authorization: `Bearer ${token.access}`,
          },
        }
      );
      toast.success("Account updated successfully");
    } catch (error) {
      toast.error("Failed to update account");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token?.access) {
      fetchPromoData();
      fetchAccountDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

 return (
   <Box
     sx={{
       minHeight: "100vh",
       background:
         "linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 50%, #e1f5fe 100%)",
       py: { xs: 4, md: 6 },
       px: { xs: 2, md: 4 },
     }}
   >
     <Box sx={{ maxWidth: 680, mx: "auto" }}>
       {/* ── Page Header ─────────────────────────────────────────────────── */}
       <Box sx={{ textAlign: "center", mb: 5 }}>
         <Box
           sx={{
             display: "inline-flex",
             alignItems: "center",
             gap: 1,
             px: 2,
             py: 0.75,
             mb: 2,
             borderRadius: 99,
             background: "rgba(2,136,209,0.1)",
             border: "1px solid rgba(2,136,209,0.2)",
           }}
         >
           <FaGift size={11} color="#0288D1" />
           <Typography
             sx={{
               fontSize: "0.68rem",
               fontWeight: 800,
               color: "#0288D1",
               textTransform: "uppercase",
               letterSpacing: "1px",
             }}
           >
             Referral Dashboard
           </Typography>
         </Box>
         <Typography
           variant="h4"
           sx={{ fontWeight: 900, color: "#0c1a2e", letterSpacing: "-0.5px" }}
         >
           Earn with LiteUpper
         </Typography>
         <Typography variant="body2" sx={{ color: "#64748b", mt: 0.75 }}>
           Share your code, grow your network, and earn real money
         </Typography>
       </Box>

       <Stack spacing={3}>
         {/* ── Promo Code ──────────────────────────────────────────────── */}
         <Paper
           elevation={0}
           sx={{
             borderRadius: 3,
             overflow: "hidden",
             border: "1px solid rgba(2,136,209,0.15)",
             boxShadow: "0 2px 12px rgba(2,136,209,0.08)",
             background: "#fff",
           }}
         >
           {/* Card header */}
           <Stack
             direction="row"
             alignItems="center"
             spacing={1.5}
             sx={{
               px: 2.5,
               py: 2,
               borderBottom: "1px solid #e2e8f0",
               background:
                 "linear-gradient(135deg, rgba(2,136,209,0.05), rgba(14,165,233,0.03))",
             }}
           >
             <Box
               sx={{
                 width: 32,
                 height: 32,
                 borderRadius: 1.5,
                 background: "linear-gradient(135deg, #0288D1, #0ea5e9)",
                 display: "flex",
                 alignItems: "center",
                 justifyContent: "center",
                 boxShadow: "0 4px 10px rgba(2,136,209,0.3)",
               }}
             >
               <FaTag size={13} color="#fff" />
             </Box>
             <Box>
               <Typography
                 sx={{ fontWeight: 800, color: "#0c1a2e", fontSize: "0.92rem" }}
               >
                 My Promo Code
               </Typography>
               <Typography variant="caption" sx={{ color: "#64748b" }}>
                 Share this code to earn referral rewards
               </Typography>
             </Box>
           </Stack>

           <Box sx={{ px: 2.5, py: 3, textAlign: "center" }}>
             <Paper
               elevation={0}
               sx={{
                 display: "inline-flex",
                 alignItems: "center",
                 gap: 2,
                 px: 3.5,
                 py: 2,
                 borderRadius: 2.5,
                 background: "rgba(2,136,209,0.06)",
                 border: "1.5px dashed rgba(2,136,209,0.3)",
               }}
             >
               <Typography
                 sx={{
                   fontWeight: 900,
                   fontSize: "1.8rem",
                   letterSpacing: "6px",
                   color: "#0288D1",
                   fontFamily: "monospace",
                 }}
               >
                 {promoCode}
               </Typography>
               <Tooltip title="Copy code" arrow>
                 <IconButton
                   size="small"
                   onClick={() => navigator.clipboard.writeText(promoCode)}
                   sx={{
                     color: "#0288D1",
                     background: "rgba(2,136,209,0.1)",
                     border: "1px solid rgba(2,136,209,0.2)",
                     borderRadius: 1.5,
                     "&:hover": { background: "rgba(2,136,209,0.18)" },
                   }}
                 >
                   <FaCopy size={13} />
                 </IconButton>
               </Tooltip>
             </Paper>
           </Box>
         </Paper>

         {/* ── Profit ──────────────────────────────────────────────────── */}
         <Paper
           elevation={0}
           sx={{
             borderRadius: 3,
             overflow: "hidden",
             border: "1px solid rgba(5,150,105,0.2)",
             boxShadow: "0 2px 12px rgba(5,150,105,0.08)",
             background: "#fff",
           }}
         >
           <Stack
             direction="row"
             alignItems="center"
             spacing={1.5}
             sx={{
               px: 2.5,
               py: 2,
               borderBottom: "1px solid #e2e8f0",
               background:
                 "linear-gradient(135deg, rgba(5,150,105,0.05), rgba(16,185,129,0.03))",
             }}
           >
             <Box
               sx={{
                 width: 32,
                 height: 32,
                 borderRadius: 1.5,
                 background: "linear-gradient(135deg, #059669, #10b981)",
                 display: "flex",
                 alignItems: "center",
                 justifyContent: "center",
                 boxShadow: "0 4px 10px rgba(5,150,105,0.3)",
               }}
             >
               <FaMoneyBillWave size={13} color="#fff" />
             </Box>
             <Box>
               <Typography
                 sx={{ fontWeight: 800, color: "#0c1a2e", fontSize: "0.92rem" }}
               >
                 Total Earnings
               </Typography>
               <Typography variant="caption" sx={{ color: "#64748b" }}>
                 Accumulated referral profit
               </Typography>
             </Box>
           </Stack>

           <Box sx={{ px: 2.5, py: 3, textAlign: "center" }}>
             <Typography
               sx={{
                 fontSize: "0.68rem",
                 fontWeight: 700,
                 color: "#94a3b8",
                 textTransform: "uppercase",
                 letterSpacing: "1px",
                 mb: 0.5,
               }}
             >
               Available Balance
             </Typography>
             <Typography
               sx={{
                 fontWeight: 900,
                 fontSize: "3rem",
                 letterSpacing: "-2px",
                 color: "#059669",
                 lineHeight: 1,
                 mb: 0.5,
               }}
             >
               ₦{Number(profit).toLocaleString()}
             </Typography>
             <Box
               sx={{
                 display: "inline-flex",
                 alignItems: "center",
                 gap: 0.6,
                 mt: 1,
                 px: 1.5,
                 py: 0.4,
                 borderRadius: 99,
                 background: "rgba(5,150,105,0.08)",
                 border: "1px solid rgba(5,150,105,0.15)",
               }}
             >
               <Box
                 sx={{
                   width: 6,
                   height: 6,
                   borderRadius: "50%",
                   background: "#10b981",
                   boxShadow: "0 0 5px #10b981",
                 }}
               />
               <Typography
                 sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#059669" }}
               >
                 Earnings Active
               </Typography>
             </Box>
           </Box>
         </Paper>

         {/* ── Referred Users ───────────────────────────────────────────── */}
         <Paper
           elevation={0}
           sx={{
             borderRadius: 3,
             overflow: "hidden",
             border: "1px solid rgba(124,58,237,0.15)",
             boxShadow: "0 2px 12px rgba(124,58,237,0.06)",
             background: "#fff",
           }}
         >
           <Stack
             direction="row"
             alignItems="center"
             justifyContent="space-between"
             sx={{
               px: 2.5,
               py: 2,
               borderBottom: "1px solid #e2e8f0",
               background:
                 "linear-gradient(135deg, rgba(124,58,237,0.05), rgba(168,85,247,0.03))",
             }}
           >
             <Stack direction="row" alignItems="center" spacing={1.5}>
               <Box
                 sx={{
                   width: 32,
                   height: 32,
                   borderRadius: 1.5,
                   background: "linear-gradient(135deg, #7c3aed, #a855f7)",
                   display: "flex",
                   alignItems: "center",
                   justifyContent: "center",
                   boxShadow: "0 4px 10px rgba(124,58,237,0.3)",
                 }}
               >
                 <FaUsers size={13} color="#fff" />
               </Box>
               <Box>
                 <Typography
                   sx={{
                     fontWeight: 800,
                     color: "#0c1a2e",
                     fontSize: "0.92rem",
                   }}
                 >
                   Referred Users
                 </Typography>
                 <Typography variant="caption" sx={{ color: "#64748b" }}>
                   People who joined using your code
                 </Typography>
               </Box>
             </Stack>
             <Box
               sx={{
                 px: 1.5,
                 py: 0.4,
                 borderRadius: 99,
                 background: "rgba(124,58,237,0.1)",
                 border: "1px solid rgba(124,58,237,0.2)",
               }}
             >
               <Typography
                 sx={{ fontSize: "0.72rem", fontWeight: 800, color: "#7c3aed" }}
               >
                 {referredUsers.length}
               </Typography>
             </Box>
           </Stack>

           <Box sx={{ px: 2.5, py: referredUsers.length === 0 ? 5 : 2 }}>
             {referredUsers.length === 0 ? (
               <Box sx={{ textAlign: "center" }}>
                 <FaUsers size={32} color="#ddd8fe" />
                 <Typography
                   sx={{
                     fontWeight: 700,
                     color: "#94a3b8",
                     mt: 1.5,
                     fontSize: "0.88rem",
                   }}
                 >
                   No referrals yet
                 </Typography>
                 <Typography variant="caption" sx={{ color: "#cbd5e1" }}>
                   Share your promo code to get started
                 </Typography>
               </Box>
             ) : (
               <Stack spacing={1}>
                 {referredUsers.map((user, index) => (
                   <Stack
                     key={index}
                     direction="row"
                     alignItems="center"
                     spacing={1.5}
                     sx={{
                       px: 2,
                       py: 1.5,
                       borderRadius: 2,
                       background: "#f8fafc",
                       border: "1px solid #e2e8f0",
                       transition: "all 0.2s",
                       "&:hover": {
                         borderColor: "rgba(124,58,237,0.25)",
                         background: "rgba(124,58,237,0.03)",
                       },
                     }}
                   >
                     <Box
                       sx={{
                         width: 32,
                         height: 32,
                         borderRadius: "50%",
                         background:
                           "linear-gradient(135deg, rgba(124,58,237,0.15), rgba(168,85,247,0.2))",
                         border: "1px solid rgba(124,58,237,0.2)",
                         display: "flex",
                         alignItems: "center",
                         justifyContent: "center",
                         flexShrink: 0,
                       }}
                     >
                       <FaUserFriends size={13} color="#7c3aed" />
                     </Box>
                     <Typography
                       sx={{
                         fontWeight: 600,
                         color: "#0c1a2e",
                         fontSize: "0.88rem",
                       }}
                     >
                       {user}
                     </Typography>
                     <Box sx={{ ml: "auto" }}>
                       <Chip
                         label="Referred"
                         size="small"
                         sx={{
                           height: 18,
                           fontSize: "0.6rem",
                           fontWeight: 700,
                           background: "rgba(5,150,105,0.1)",
                           color: "#059669",
                           border: "1px solid rgba(5,150,105,0.2)",
                         }}
                       />
                     </Box>
                   </Stack>
                 ))}
               </Stack>
             )}
           </Box>
         </Paper>

         {/* ── Account Details ──────────────────────────────────────────── */}
         <Paper
           elevation={0}
           sx={{
             borderRadius: 3,
             overflow: "hidden",
             border: "1px solid rgba(2,136,209,0.15)",
             boxShadow: "0 2px 12px rgba(2,136,209,0.08)",
             background: "#fff",
           }}
         >
           <Stack
             direction="row"
             alignItems="center"
             spacing={1.5}
             sx={{
               px: 2.5,
               py: 2,
               borderBottom: "1px solid #e2e8f0",
               background:
                 "linear-gradient(135deg, rgba(2,136,209,0.05), rgba(14,165,233,0.03))",
             }}
           >
             <Box
               sx={{
                 width: 32,
                 height: 32,
                 borderRadius: 1.5,
                 background: "linear-gradient(135deg, #0288D1, #0ea5e9)",
                 display: "flex",
                 alignItems: "center",
                 justifyContent: "center",
                 boxShadow: "0 4px 10px rgba(2,136,209,0.3)",
               }}
             >
               <FaUniversity size={13} color="#fff" />
             </Box>
             <Box>
               <Typography
                 sx={{ fontWeight: 800, color: "#0c1a2e", fontSize: "0.92rem" }}
               >
                 Account Details
               </Typography>
               <Typography variant="caption" sx={{ color: "#64748b" }}>
                 Where your earnings will be sent
               </Typography>
             </Box>
           </Stack>

           <Box sx={{ px: 2.5, py: 3 }}>
             <Stack spacing={2}>
               <Box>
                 <Typography
                   sx={{
                     fontSize: "0.68rem",
                     fontWeight: 700,
                     color: "#64748b",
                     textTransform: "uppercase",
                     letterSpacing: "0.6px",
                     mb: 0.75,
                   }}
                 >
                   Bank Name
                 </Typography>
                 <TextField
                   fullWidth
                   size="small"
                   placeholder="e.g. First Bank"
                   value={bankName}
                   onChange={(e) => setBankName(e.target.value)}
                   InputProps={{
                     startAdornment: (
                       <InputAdornment position="start">
                         <FaUniversity size={13} color="#0288D1" />
                       </InputAdornment>
                     ),
                   }}
                   sx={{
                     "& .MuiOutlinedInput-root": {
                       color: "#0c1a2e",
                       borderRadius: 2,
                       background: "#f8fafc",
                       "& fieldset": { borderColor: "rgba(2,136,209,0.2)" },
                       "&:hover fieldset": { borderColor: "#0288D1" },
                       "&.Mui-focused fieldset": { borderColor: "#0288D1" },
                     },
                     "& input::placeholder": {
                       color: "#94a3b8",
                       fontSize: "0.85rem",
                     },
                   }}
                 />
               </Box>

               <Box>
                 <Typography
                   sx={{
                     fontSize: "0.68rem",
                     fontWeight: 700,
                     color: "#64748b",
                     textTransform: "uppercase",
                     letterSpacing: "0.6px",
                     mb: 0.75,
                   }}
                 >
                   Account Name
                 </Typography>
                 <TextField
                   fullWidth
                   size="small"
                   placeholder="e.g. John Doe"
                   value={accountName}
                   onChange={(e) => setAccountName(e.target.value)}
                   InputProps={{
                     startAdornment: (
                       <InputAdornment position="start">
                         <FaUser size={13} color="#0288D1" />
                       </InputAdornment>
                     ),
                   }}
                   sx={{
                     "& .MuiOutlinedInput-root": {
                       color: "#0c1a2e",
                       borderRadius: 2,
                       background: "#f8fafc",
                       "& fieldset": { borderColor: "rgba(2,136,209,0.2)" },
                       "&:hover fieldset": { borderColor: "#0288D1" },
                       "&.Mui-focused fieldset": { borderColor: "#0288D1" },
                     },
                     "& input::placeholder": {
                       color: "#94a3b8",
                       fontSize: "0.85rem",
                     },
                   }}
                 />
               </Box>

               <Box>
                 <Typography
                   sx={{
                     fontSize: "0.68rem",
                     fontWeight: 700,
                     color: "#64748b",
                     textTransform: "uppercase",
                     letterSpacing: "0.6px",
                     mb: 0.75,
                   }}
                 >
                   Account Number
                 </Typography>
                 <TextField
                   fullWidth
                   size="small"
                   placeholder="10-digit account number"
                   value={accountNum}
                   onChange={(e) => setAccountNum(e.target.value)}
                   InputProps={{
                     startAdornment: (
                       <InputAdornment position="start">
                         <FaCreditCard size={13} color="#0288D1" />
                       </InputAdornment>
                     ),
                   }}
                   sx={{
                     "& .MuiOutlinedInput-root": {
                       color: "#0c1a2e",
                       borderRadius: 2,
                       background: "#f8fafc",
                       "& fieldset": { borderColor: "rgba(2,136,209,0.2)" },
                       "&:hover fieldset": { borderColor: "#0288D1" },
                       "&.Mui-focused fieldset": { borderColor: "#0288D1" },
                     },
                     "& input::placeholder": {
                       color: "#94a3b8",
                       fontSize: "0.85rem",
                     },
                   }}
                 />
               </Box>
             </Stack>
           </Box>

           <Stack
             direction="row"
             sx={{
               px: 2.5,
               py: 2,
               borderTop: "1px solid #e2e8f0",
               background: "#f8fafc",
             }}
           >
             <Button
               variant="contained"
               onClick={handleUpdateAccount}
               disabled={loading}
               startIcon={loading ? null : <FaCheck size={12} />}
               sx={{
                 borderRadius: 2,
                 textTransform: "none",
                 fontWeight: 700,
                 fontSize: "0.85rem",
                 px: 3,
                 background: "linear-gradient(135deg, #0288D1, #0ea5e9)",
                 boxShadow: "0 4px 12px rgba(2,136,209,0.25)",
                 "&:hover": {
                   background: "linear-gradient(135deg, #0277bd, #0288D1)",
                 },
                 "&:disabled": { opacity: 0.5 },
               }}
             >
               {loading ? (
                 <Stack direction="row" alignItems="center" spacing={1}>
                   <CircularProgress size={14} sx={{ color: "#fff" }} />
                   <span>Updating…</span>
                 </Stack>
               ) : (
                 "Update Account"
               )}
             </Button>
           </Stack>
         </Paper>
       </Stack>
     </Box>
   </Box>
 );
}
